/**
 * The three chips of the users-admin screens. AUTHORED (no Figma exists for the
 * admin surface); every pair is assembled from tokens the app already measures.
 *
 * ===========================================================================
 * COLOUR DECISIONS, WITH THE ARITHMETIC (asserted in admin-users.contrast.test)
 * ===========================================================================
 * · ACTIVE — the `status/success` pill pair: #187e48 on #def8e9 = 4.55:1 (the
 *   AA nudge ③ pair, mode-invariant). Active is a positive operational state
 *   and this is the app's one "good" pair.
 * · DISABLED — a BORDERED, UNFILLED chip in `text/secondary`: 4.64:1 light /
 *   5.83:1 dark against `bg/surface`. Deliberately NOT the danger pair —
 *   disabled is an administrative state, not an alarm, and painting it red
 *   would make every former employee's row shout. Deliberately not `bg/subtle`
 *   fill either: `text/secondary` on `bg/subtle` measures 4.40:1 in light mode
 *   — under the bar — which is also why the sidebar's country pill (the
 *   precedent this chip copies) is bordered and unfilled.
 * · ROLE (country×role) — the same bordered treatment, with the country code
 *   in the mono ramp the way this app always writes a country (`CountrySwitcher`,
 *   the brand block pill).
 * · GROUP ADMIN — bordered `accent/indigo` border with `text/secondary` text.
 *   NOT indigo text: `accent/indigo` on `bg/surface` is 4.22:1 light — under
 *   the bar for 12px text — so indigo carries the border (a non-text use, 3:1
 *   floor, passes at 4.22/3.90) and the words stay in a passing text colour.
 */

import { cn } from '../../lib/cn';
import type { RoleAssignment, UserStatus } from './users-api';

const CHIP_BASE =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium';

export function StatusChip({ status }: { status: UserStatus }): React.JSX.Element {
  if (status === 'active') {
    return (
      <span className={cn(CHIP_BASE, 'bg-ok-bg text-ok-fg')}>
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
        Active
      </span>
    );
  }
  return (
    <span className={cn(CHIP_BASE, 'border border-fg-muted text-fg-secondary')}>
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      Disabled
    </span>
  );
}

/**
 * One country×role assignment: `CD Country Manager`. The code is the visible token
 * (mono, the house way to write a country); the full country name would turn a
 * five-role row into a paragraph.
 */
export function RoleChip({ assignment }: { assignment: RoleAssignment }): React.JSX.Element {
  return (
    <span
      className={cn(
        CHIP_BASE,
        'border border-fg-muted py-0.5 font-normal text-fg-secondary',
      )}
    >
      <span className="font-mono text-mono">{assignment.country}</span>
      {assignment.role}
    </span>
  );
}

/** A cluster of role chips, or the honest word for none. */
export function RoleChips({
  roles,
  isGroupAdmin,
}: {
  roles: readonly RoleAssignment[];
  isGroupAdmin: boolean;
}): React.JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {isGroupAdmin ? <GroupAdminChip /> : null}
      {roles.map((assignment) => (
        <RoleChip key={`${assignment.country}-${assignment.role}`} assignment={assignment} />
      ))}
      {roles.length === 0 && !isGroupAdmin ? (
        // A real state (a just-created account before assignment), not an
        // error. Saying "No roles" beats an empty cell that reads as a
        // rendering bug.
        <span className="text-label text-fg-secondary">No roles</span>
      ) : null}
    </span>
  );
}

/**
 * `grp:true` — every permission in every country. Not a role row, so not a
 * RoleChip: the indigo border marks it as an authority statement rather than a
 * country assignment.
 */
export function GroupAdminChip(): React.JSX.Element {
  return (
    <span className={cn(CHIP_BASE, 'border border-indigo py-0.5 text-fg-secondary')}>
      Group admin
    </span>
  );
}
